import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Backdrop,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Description as LetterIcon,
  LocalHospital as LomnIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../../app/hooks';
import {
  useGetGapAnalysisQuery,
  useRunGapAnalysisMutation,
  useToggleGapAnalysisItemMutation,
  useEditGapCriterionMutation,
  useGenerateQueryLetterMutation,
  useDownloadQueryLetterPdfMutation,
  useGenerateLomnMutation,
  useDownloadLomnPdfMutation,
  useUpdateCaseMutation,
  updateCase,
} from '../../../../../services/MedNecService';
import { useGetCenterQuery } from '../../../../../services/ConfigurationService';
import { useMedNecContext } from '../../../hooks/useMedNecContext';
import CriterionItem from './CriterionItem';
import AddCriterionForm from './AddCriterionForm';
import InpatientAnalysisPanel from './InpatientAnalysisPanel';
import EmergentAnalysisPanel from './EmergentAnalysisPanel';
import CheckableList from './CheckableList';
import QueryLetterPreviewModal from './QueryLetterPreviewModal';
import LomnPreviewModal from './LomnPreviewModal';

const RISK_CONFIG: Record<string, { color: 'success' | 'warning' | 'error'; label: string }> = {
  ALIK: { color: 'success', label: 'Approval Likely' },
  NSTR: { color: 'warning', label: 'Needs Strengthening' },
  CDRK: { color: 'error', label: 'Critical Denial Risk' },
};

const GAP_POLL_INTERVAL_MS = 4000;

function toFriendlyRunError(err: unknown): string {
  const candidate = err as { status?: number | string; data?: unknown } | null;
  const raw = candidate && 'data' in (candidate ?? {}) ? String(candidate?.data ?? '') : '';
  if (candidate?.status === 408 || candidate?.status === 504 || raw.toLowerCase().includes('stream timeout')) {
    return 'The assessment is taking longer than expected. It may still be running — please wait a moment and refresh.';
  }
  if (candidate?.status === 409 || raw.toLowerCase().includes('already in progress')) {
    return 'An assessment is already in progress for this case.';
  }
  return raw || 'Failed to run gap analysis';
}

const GapAnalysisSection: React.FC = () => {
  const currentCase = useAppSelector(s => s.medNecCase.currentCase);
  const caseId = currentCase?.id ?? '';
  const isDirectAdmit = currentCase?.isDirectAdmit ?? false;

  // Poll the status endpoint while a background run is in progress
  const [pollInterval, setPollInterval] = useState(0);
  const { data: gapStatus, isLoading, error } = useGetGapAnalysisQuery(caseId, {
    skip: !caseId,
    pollingInterval: pollInterval,
  });
  const gapAnalysis = gapStatus?.analysis ?? undefined;
  const isServerRunning = gapStatus?.runStatus === 'Running';
  const serverRunError = gapStatus?.runStatus === 'Failed' ? (gapStatus.runError ?? 'The assessment failed. Please try again.') : null;

  useEffect(() => {
    setPollInterval(isServerRunning ? GAP_POLL_INTERVAL_MS : 0);
  }, [isServerRunning]);

  const [runAnalysis, { isLoading: isStarting }] = useRunGapAnalysisMutation();
  const isRunning = isStarting || isServerRunning;
  const [toggleItem] = useToggleGapAnalysisItemMutation();
  const [editCriterion] = useEditGapCriterionMutation();
  const [startError, setStartError] = useState<string | null>(null);
  const runError = startError ?? serverRunError;

  // ── Physician Query Letter ───────────────────────────────────────
  const { company, centerId, isConfigured } = useMedNecContext();
  const { data: centerData } = useGetCenterQuery({ id: centerId, company }, { skip: !isConfigured });
  const [generateLetter, { isLoading: isGenerating }] = useGenerateQueryLetterMutation();
  const [downloadPdf, { isLoading: isDownloading }] = useDownloadQueryLetterPdfMutation();
  const [generateLomnLetter, { isLoading: isGeneratingLomn }] = useGenerateLomnMutation();
  const [downloadLomnPdf, { isLoading: isDownloadingLomn }] = useDownloadLomnPdfMutation();
  const [letterHtml, setLetterHtml] = useState('');
  const [letterModalOpen, setLetterModalOpen] = useState(false);
  const [lomnHtml, setLomnHtml] = useState('');
  const [lomnModalOpen, setLomnModalOpen] = useState(false);

  // ── Direct Admit Toggle ──────────────────────────────────────────
  const dispatch = useAppDispatch();
  const [saveCaseUpdate] = useUpdateCaseMutation();
  const handleDirectAdmitChange = useCallback(async (checked: boolean) => {
    dispatch(updateCase({ isDirectAdmit: checked }));
    if (caseId && currentCase) {
      await saveCaseUpdate({ id: caseId, body: { ...currentCase, isDirectAdmit: checked } });
    }
  }, [dispatch, caseId, currentCase, saveCaseUpdate]);

  const handleToggle = useCallback(
    (section: 'preAuth' | 'actionItem' | 'criterionAction', index: number, criterionIndex?: number) => {
      toggleItem({ id: caseId, section, index, criterionIndex });
    },
    [caseId, toggleItem],
  );

  const handleCriterionToggle = useCallback(
    (criterionIndex: number, actionIndex: number) => {
      handleToggle('criterionAction', actionIndex, criterionIndex);
    },
    [handleToggle],
  );

  const handleStatusChange = useCallback(
    (index: number, status: string) => {
      editCriterion({ id: caseId, operation: 'updateStatus', index, status });
    },
    [caseId, editCriterion],
  );

  const handleCommentSave = useCallback(
    (index: number, comment: string | null) => {
      editCriterion({ id: caseId, operation: 'setComment', index, comment: comment ?? undefined });
    },
    [caseId, editCriterion],
  );

  const handleRemoveCriterion = useCallback(
    (index: number) => {
      editCriterion({ id: caseId, operation: 'removeCriterion', index });
    },
    [caseId, editCriterion],
  );

  const handleAddCriterion = useCallback((description: string, status: string) => {
    editCriterion({ id: caseId, operation: 'addCriterion', index: 0, description, status });
  }, [caseId, editCriterion]);

  const handleRunAnalysis = async () => {
    setStartError(null);
    try {
      await runAnalysis({ id: caseId }).unwrap();
      // 202 Accepted — the run continues in the background; polling picks up the result
    } catch (err) {
      setStartError(toFriendlyRunError(err));
    }
  };

  const handleGenerateLetter = useCallback(async () => {
    if (!centerData) return;
    try {
      const result = await generateLetter({
        id: caseId,
        facility: {
          name: centerData.name,
          address1: centerData.address?.address1 ?? '',
          address2: centerData.address?.address2,
          city: centerData.address?.city ?? '',
          state: centerData.address?.state ?? '',
          zip: centerData.address?.zip ?? '',
          phone: centerData.contactInfo?.phone,
          email: centerData.contactInfo?.email,
        },
      }).unwrap();
      setLetterHtml(result.html);
      setLetterModalOpen(true);
    } catch {
      setStartError('Failed to generate query letter');
    }
  }, [caseId, centerData, generateLetter]);

  const handleDownloadPdf = useCallback(async (editedHtml: string) => {
    if (!centerData || !currentCase) return;
    const blob = await downloadPdf({
      html: editedHtml,
      facility: {
        name: centerData.name,
        address1: centerData.address?.address1 ?? '',
        address2: centerData.address?.address2,
        city: centerData.address?.city ?? '',
        state: centerData.address?.state ?? '',
        zip: centerData.address?.zip ?? '',
        phone: centerData.contactInfo?.phone,
        email: centerData.contactInfo?.email,
      },
      patientName: currentCase.patient ? `${currentCase.patient.lastName}, ${currentCase.patient.firstName}` : '',
      dateOfBirth: currentCase.patient?.dateOfBirth ?? '',
      payerName: currentCase.payerName ?? '',
      procedures: currentCase.cptCodes?.join(', ') ?? '',
      caseReference: caseId,
    }).unwrap();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }, [caseId, centerData, currentCase, downloadPdf]);

  const handleGenerateLomn = useCallback(async () => {
    if (!centerData) return;
    try {
      const result = await generateLomnLetter({
        id: caseId,
        facility: {
          name: centerData.name,
          address1: centerData.address?.address1 ?? '',
          address2: centerData.address?.address2,
          city: centerData.address?.city ?? '',
          state: centerData.address?.state ?? '',
          zip: centerData.address?.zip ?? '',
          phone: centerData.contactInfo?.phone,
          email: centerData.contactInfo?.email,
        },
      }).unwrap();
      setLomnHtml(result.html);
      setLomnModalOpen(true);
    } catch {
      setStartError('Failed to generate Letter of Medical Necessity');
    }
  }, [caseId, centerData, generateLomnLetter]);

  const handleDownloadLomnPdf = useCallback(async (editedHtml: string) => {
    if (!centerData || !currentCase) return;
    const blob = await downloadLomnPdf({
      html: editedHtml,
      facility: {
        name: centerData.name,
        address1: centerData.address?.address1 ?? '',
        address2: centerData.address?.address2,
        city: centerData.address?.city ?? '',
        state: centerData.address?.state ?? '',
        zip: centerData.address?.zip ?? '',
        phone: centerData.contactInfo?.phone,
        email: centerData.contactInfo?.email,
      },
      patientName: currentCase.patient ? `${currentCase.patient.lastName}, ${currentCase.patient.firstName}` : '',
      dateOfBirth: currentCase.patient?.dateOfBirth ?? '',
      payerName: currentCase.payerName ?? '',
      procedures: currentCase.cptCodes?.join(', ') ?? '',
      caseReference: caseId,
    }).unwrap();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }, [caseId, centerData, currentCase, downloadLomnPdf]);

  if (!caseId) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <Paper sx={{ p: 3, textAlign: 'center', border: '1px dashed', borderColor: 'error.light' }}>
          <Alert severity="error" sx={{ mb: 2 }}>Failed to load assessment. You can retry below.</Alert>
          {runError && <Alert severity="warning" sx={{ mb: 2 }}>{runError}</Alert>}
          <Button
            variant="contained"
            startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <RunIcon />}
            onClick={handleRunAnalysis}
            disabled={isRunning}
          >
            {isRunning ? 'Analyzing...' : 'Retry Assessment'}
          </Button>
        </Paper>
      </Box>
    );
  }

  // Run in progress and no previous analysis to show — progress panel
  if (!gapAnalysis && isRunning) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <Paper sx={{ p: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <CircularProgress size={28} sx={{ mb: 1.5 }} />
          <Typography variant="h6" gutterBottom>Assessment In Progress</Typography>
          <Typography variant="body2" color="text.secondary">
            Documents are being transcribed and evaluated against payer criteria.
            This can take a few minutes for large packets — you can leave this page and come back.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // No analysis yet — show run button
  if (!gapAnalysis) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <Paper sx={{ p: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>No Assessment Yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Run an AI-powered assessment to evaluate clinical documentation against payer criteria.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isDirectAdmit}
                onChange={e => handleDirectAdmitChange(e.target.checked)}
                size="small"
              />
            }
            label="Direct Admit"
            sx={{ mb: 2 }}
          />
          {isDirectAdmit && (
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              Emergent admission criteria will be evaluated alongside standard gap analysis.
            </Alert>
          )}
          {runError && <Alert severity="error" sx={{ mb: 2 }}>{runError}</Alert>}
          <Button
            variant="contained"
            startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <RunIcon />}
            onClick={handleRunAnalysis}
            disabled={isRunning}
          >
            {isRunning ? 'Analyzing...' : 'Run Assessment'}
          </Button>
        </Paper>
      </Box>
    );
  }

  const riskConfig = RISK_CONFIG[gapAnalysis.overallRiskStatus] || RISK_CONFIG.NSTR;
  const gapCount = gapAnalysis.criteria.filter(c => c.status === 'GAP' || c.status === 'CGAP').length;
  const metCount = gapAnalysis.criteria.filter(c => c.status === 'MET').length;
  const allCriteriaMet = gapAnalysis.criteria.length > 0 && gapAnalysis.criteria.every(c => c.status === 'MET');

  return (
    <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Re-run failure */}
      {runError && (
        <Alert severity="error" onClose={startError ? () => setStartError(null) : undefined}>
          {runError}
        </Alert>
      )}

      {/* Re-run in progress */}
      {isRunning && (
        <Alert severity="info" icon={<CircularProgress size={18} />}>
          Re-running assessment in the background — results will refresh automatically.
        </Alert>
      )}

      {/* Direct Admit Toggle */}
      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isDirectAdmit}
              onChange={e => handleDirectAdmitChange(e.target.checked)}
              size="small"
            />
          }
          label="Direct Admit"
          sx={{ m: 0 }}
        />
        {isDirectAdmit && (
          <Typography variant="caption" color="warning.main">
            Emergent criteria will be evaluated. Re-run assessment after toggling.
          </Typography>
        )}
      </Paper>

      {/* Overall Risk Banner */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: `${riskConfig.color}.50` }}>
        <Chip label={riskConfig.label} color={riskConfig.color} sx={{ fontWeight: 700 }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {metCount} met, {gapCount} gaps out of {gapAnalysis.criteria.length} criteria
          {gapAnalysis.policyName && ` — ${gapAnalysis.policyName}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Confidence: {Math.round(gapAnalysis.overallConfidence * 100)}%
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={isRunning ? <CircularProgress size={14} /> : <RunIcon />}
          onClick={handleRunAnalysis}
          disabled={isRunning}
        >
          Re-run
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={isGenerating ? <CircularProgress size={14} /> : <LetterIcon />}
          onClick={handleGenerateLetter}
          disabled={isGenerating || !centerData}
        >
          Query Letter
        </Button>
        {allCriteriaMet && (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={isGeneratingLomn ? <CircularProgress size={14} /> : <LomnIcon />}
            onClick={handleGenerateLomn}
            disabled={isGeneratingLomn || !centerData}
          >
            Generate LOMN
          </Button>
        )}
      </Paper>

      {/* Payer Policy Reference */}
      {(gapAnalysis.policyName || gapAnalysis.policyNumber) && (
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f5f5f5' }}>
          <PolicyIcon color="action" />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {gapAnalysis.policyName || 'Unknown Policy'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {gapAnalysis.policyNumber && `Policy #${gapAnalysis.policyNumber}`}
              {gapAnalysis.policyNumber && gapAnalysis.policyEffectiveDate && ' — '}
              {gapAnalysis.policyEffectiveDate && `Effective: ${gapAnalysis.policyEffectiveDate}`}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Criteria */}
      {gapAnalysis.criteria.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Criteria Evaluation</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {gapAnalysis.criteria.map((c, i) => (
              <CriterionItem
                key={i}
                criterion={c}
                criterionIndex={i}
                onToggleAction={handleCriterionToggle}
                onStatusChange={handleStatusChange}
                onCommentSave={handleCommentSave}
                onRemove={handleRemoveCriterion}
              />
            ))}
          </Box>
          <AddCriterionForm onAdd={handleAddCriterion} />
        </Box>
      )}

      {/* Inpatient Analysis */}
      {gapAnalysis.inpatientAnalysis && (
        <InpatientAnalysisPanel analysis={gapAnalysis.inpatientAnalysis} />
      )}

      {/* Emergent Admission Analysis */}
      {gapAnalysis.emergentAnalysis && (
        <EmergentAnalysisPanel analysis={gapAnalysis.emergentAnalysis} />
      )}

      {/* Pre-Auth Checklist */}
      <CheckableList
        title="Pre-Auth Checklist"
        items={gapAnalysis.preAuthChecklist}
        onToggle={(i) => handleToggle('preAuth', i)}
      />

      {/* Action Items Summary */}
      <CheckableList
        title="Action Items"
        items={gapAnalysis.actionItemsSummary}
        onToggle={(i) => handleToggle('actionItem', i)}
      />

      {/* Analyzed timestamp */}
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
        Analyzed: {new Date(gapAnalysis.analyzedAt).toLocaleString()}
        {gapAnalysis.documentsReviewed.length > 0 && ` — ${gapAnalysis.documentsReviewed.length} document(s) reviewed`}
      </Typography>

      {/* Physician Query Letter Modal */}
      <QueryLetterPreviewModal
        open={letterModalOpen}
        html={letterHtml}
        onClose={() => setLetterModalOpen(false)}
        onDownloadPdf={handleDownloadPdf}
        isDownloading={isDownloading}
      />

      {/* LOMN Preview Modal */}
      <LomnPreviewModal
        open={lomnModalOpen}
        html={lomnHtml}
        onClose={() => setLomnModalOpen(false)}
        onDownloadPdf={handleDownloadLomnPdf}
        isDownloading={isDownloadingLomn}
      />

      {/* Full-page loading overlays */}
      <Backdrop open={isRunning} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Running Gap Analysis...</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>Evaluating payer policy criteria with AI. This may take a moment.</Typography>
      </Backdrop>
      <Backdrop open={isGeneratingLomn} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Generating Letter of Medical Necessity...</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>Building clinical narrative with AI. This may take a moment.</Typography>
      </Backdrop>
      <Backdrop open={isGenerating} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Generating Physician Query Letter...</Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>Preparing letter content. This may take a moment.</Typography>
      </Backdrop>

    </Box>
  );
};

export default GapAnalysisSection;
